/**
 * Module: Overflow Scroll
 *
 * Angular module to support an overflow scroll where touch devices
 * can swipe/pan items and non-touch inputs allow for buttons to move
 * the items along a carousel to be visible.
 */
export default angular.module('overflowScroll', ['duScroll'])
    /**
     * @ngdoc controller
     * @name overflowScroll.controller:overflowScroll
     * @description
     * Controls the scroll interaction and index positioning
     */
    .controller('overflowScroll', function ($scope, AnalyticsService) {
        var is_scrolling = false;
        $scope.element = null;
        $scope.elements = [];
        $scope.has_more = false;
        $scope.has_less = false;
        // Lock overflow scroll from being usable
        function lockScroll() {
            is_scrolling = true;
        }
        // Allow overflow scroll to run again
        function unlockScroll() {
            is_scrolling = false;
        }
        // Return current index, based on scroll position
        function getCurrentIndexFromScroll() {
            if ($scope.element.scrollLeft() <= 0) {
                return 0;
            }
            return Math.floor($scope.element.scrollLeft() / getSingleItemWidth());
        }
        // Return the width of the scrolling container
        function getCurrentContainerWidth() {
            if ($scope.element.length) {
                return $scope.element[0].scrollWidth;
            }
            return 0;
        }
        // Get the width of the current elements
        function getSingleItemWidth() {
            if ($scope.elements.length) {
                var item = $scope.elements[0][0];
                var style = window.getComputedStyle(item);
                var width = parseFloat(style.width);
                var margin = parseFloat(style.marginRight);
                return Math.round(width + margin);
            }
            return 0;
        }
        // Return the px position for an element index
        function getScrollPositionForElement(index) {
            return getSingleItemWidth() * index;
        }
        // Return next index, from current
        function getNextIndex() {
            return getCurrentIndexFromScroll() + 1;
        }
        // Return previous index, from current
        function getPreviousIndex() {
            if ((getCurrentIndexFromScroll() * getSingleItemWidth()) === $scope.element.scrollLeft()) {
                return getCurrentIndexFromScroll() - 1;
            }
            return getCurrentIndexFromScroll();
        }
        // Update the current state to apply classes where required
        $scope.updateState = function () {
            var min_offset = $scope.element.scrollLeft();
            var max_offset = min_offset + ($scope.element[0].offsetWidth || $scope.element[0].clientWidth) + 1;
            $scope.has_less = min_offset > 0;
            $scope.has_more = max_offset < getCurrentContainerWidth();
        };
        // Return all current items
        $scope.getItems = function () {
            return $scope.elements;
        };
        // Return the current index of the overflow scroll
        $scope.getCurrentIndex = function () {
            // final item can't cross scroll threshold, allow a 25%
            // offset to catch any hits
            if ((getCurrentContainerWidth() - $scope.element.scrollLeft() - getSingleItemWidth() * 1.25) < 0) {
                return $scope.getItems().length - 1;
            }
            return getCurrentIndexFromScroll();
        };
        // Show item in the overflow scroll view
        $scope.showIndex = function (index) {
            if (!is_scrolling) {
                var offset = getScrollPositionForElement(index);
                lockScroll();
                $scope.element.scrollTo(offset, 0, 500)
                    .then(unlockScroll, unlockScroll);
            }
        };
        // Show the next item in the overflow scroll view
        $scope.showNext = function () {
            if (!is_scrolling) {
                var offset = getScrollPositionForElement(getNextIndex());
                lockScroll();
                $scope.element.scrollTo(offset, 0, 500)
                    .then(unlockScroll, unlockScroll);
                if ($scope.has_more) {
                    AnalyticsService.trackEvent('User Interaction', 'Pagination', 'Next');
                }
            }
        };
        // Show the previous item in the overflow scroll view
        $scope.showPrevious = function () {
            if (!is_scrolling) {
                var offset = getScrollPositionForElement(getPreviousIndex());
                lockScroll();
                $scope.element.scrollTo(offset, 0, 500)
                    .then(unlockScroll, unlockScroll);
                 if ($scope.has_less) {
                    AnalyticsService.trackEvent('User Interaction', 'Pagination', 'Previous');
                 }
            }
        };
    })
    /**
     * @ngdoc directive
     * @name overflowScroll.directive:overflowScroll
     * @restrict A
     * @description
     * Set the scope and controller for an overflow scroll
     */
    .directive('overflowScroll', function (AnalyticsService) {
        return {
            controller: 'overflowScroll',
            scope: true,
            restrict: 'A',
            link: function ($scope, $element) {
                $scope.$watch('has_less', function () {
                    $element.toggleClass('overflow-scroll--has-less', $scope.has_less);
                    if (!$scope.has_less) {
                        AnalyticsService.trackEvent('User Interaction', 'Pagination', 'Start');
                    }
                });
                $scope.$watch('has_more', function () {
                    $element.toggleClass('overflow-scroll--has-more', $scope.has_more);
                    if (!$scope.has_more) {
                        AnalyticsService.trackEvent('User Interaction', 'Pagination', 'End');
                    }
                });
                $scope.updateState();
                if (document && document.documentMode < 10) {
                    $element.addClass('no-overflow-style');
                }
            }
        };
    })
    /**
     * @ngdoc directive
     * @name overflowScroll.directive:scrollContainer
     * @restrict A
     * @description
     * Set the container scope for what should scroll
     */
    // @ngInject
    .directive('overflowScrollContainer', function ($window, $timeout) {
        return {
            link: function ($scope, $element) {
                $scope.element = $element;
                function updateState() {
                    $timeout(function () {
                        $scope.updateState();
                    });
                }
                $element.on('scroll', updateState);
                angular.element($window).on('resize', updateState);
            }
        };
    })
    /**
     * @ngdoc directive
     * @name overflowScroll.directive:scrollItem
     * @restrict A
     * @description
     * Add another scroll item for working out positioning
     */
    // @ngInject
    .directive('overflowScrollItem', function () {
        return {
            link: function ($scope, $element, attrs) {
                $scope.elements.push($element);
            }
        };
    })
    /**
     * @ngdoc directive
     * @name overflowScroll.directive:scrollNext
     * @restrict A
     * @description
     * Smooth scroll the scroll view to the next item
     */
    // @ngInject
    .directive('overflowScrollNext', function () {
        return {
            restrict: 'A',
            link: function ($scope, $element) {
                $element.on('click', function (e) {
                    e.preventDefault();
                    $scope.showNext();
                });
                $scope.$watch(function () { return $scope.has_more; }, function (has_more) {
                    $element.toggleClass('navigation-item--inactive', !has_more);
                });
            }
        };
    })
    /**
     * @ngdoc directive
     * @name overflowScroll.directive:scrollPrevious
     * @restrict A
     * @description
     * Smooth scroll the scroll view to the previous item
     */
    // @ngInject
    .directive('overflowScrollPrevious', function () {
        return {
            restrict: 'A',
            link: function ($scope, $element) {
                $element.on('click', function (e) {
                    e.preventDefault();
                    $scope.showPrevious();
                });
                $scope.$watch(function () { return $scope.has_less; }, function (has_less) {
                    $element.toggleClass('navigation-item--inactive', !has_less);
                });
            }
        };
    })
    /**
     * @ngdoc directive
     * @name overflowScroll.directive:overflowScrollPagination
     * @restrict A
     * @description
     * Smooth scroll the scroll view to the next item
     */
    // @ngInject
    .directive('overflowScrollPaginationItem', function ($document, AnalyticsService) {
        return {
            restrict: 'A',
            link: function ($scope, $element, attrs) {
                var index  = parseInt(attrs.overflowScrollPaginationItem, 10);
                $element.on('click', function (e) {
                    e.preventDefault();
                    $scope.showIndex(index);
                    AnalyticsService.trackEvent('User Interaction', 'Pagination', index);
                });
                $scope.$watch($scope.getCurrentIndex, function (currentIndex) {
                    $element.toggleClass('pagination-item--active', index === currentIndex);
                });
            }
        };
    });
